import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";

const AuthContext = createContext();

function parseJwt(token) {
  if (!token) return null;
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("token"));
  const [user, setUser] = useState(() =>
    parseJwt(localStorage.getItem("token"))
  );
  const [logoutCallbacks, setLogoutCallbacks] = useState([]);

  useEffect(() => {
    if (token) {
      setUser(parseJwt(token));
    } else {
      setUser(null);
    }
  }, [token]);

  // Listen for token changes in other tabs
  useEffect(() => {
    const handleStorage = () => {
      const t = localStorage.getItem("token");
      setToken(t);
      setUser(parseJwt(t));
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const login = useCallback((jwt) => {
    localStorage.setItem("token", jwt);
    setToken(jwt);
    setUser(parseJwt(jwt));
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    // Execute all logout callbacks
    logoutCallbacks.forEach((callback) => callback());
  }, [logoutCallbacks]);

  const registerLogoutCallback = useCallback((callback) => {
    setLogoutCallbacks((prev) => [...prev, callback]);
    // Return cleanup function
    return () => {
      setLogoutCallbacks((prev) => prev.filter((cb) => cb !== callback));
    };
  }, []);

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider
      value={{ user, token, isAuthenticated, login, logout, registerLogoutCallback }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

// GoogleAuthContext for global Google connection/profile state
import axios from "axios";

const GoogleAuthContext = createContext();

function clearAvatarSessionCache(userKey) {
  if (userKey) {
    sessionStorage.removeItem(`pagetest.avatarUrl:${userKey}`);
    return;
  }
  Object.keys(sessionStorage)
    .filter((key) => key.startsWith("pagetest.avatarUrl:"))
    .forEach((key) => sessionStorage.removeItem(key));
}

export function GoogleAuthProvider({ children }) {
  const { user, isAuthenticated, login } = useAuth();
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleProfile, setGoogleProfile] = useState(null);
  const [googleAccessToken, setGoogleAccessToken] = useState("");
  const [avatarUrl, setAvatarUrl] = useState(null); // Proxied avatar URL (avoids Google rate limits)

  // Force reload if redirected from Google OAuth
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("googleConnected")) {
      clearAvatarSessionCache();
      // Refresh JWT token to get updated hasGoogleConnected flag
      const refreshToken = async () => {
        try {
          const token = localStorage.getItem("token");
          if (token) {
            const res = await axios.post(
              `${
                import.meta.env.VITE_BACKEND_URL
              }/api/auth/profile/refresh-token`,
              {},
              {
                headers: { Authorization: `Bearer ${token}` },
                withCredentials: true,
              }
            );
            login(res.data.token); // Update the JWT token
          }
        } catch (error) {
          console.error("Failed to refresh token:", error);
        }
      };

      refreshToken().then(() => {
        const params = new URLSearchParams(window.location.search);
        params.delete("googleConnected");
        const qs = params.toString();
        window.location.href =
          window.location.pathname + (qs ? `?${qs}` : "");
      });
    }
  }, [login]);

  // Silent refresh on app load if authenticated
  useEffect(() => {
    if (isAuthenticated && user?.email) {
      axios
        .post(
          `${import.meta.env.VITE_BACKEND_URL}/auth/google/silent-refresh`,
          { email: user.email },
          {
            withCredentials: true,
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          },
        )
        .then((res) => {
          setGoogleConnected(true);
          setGoogleAccessToken(res.data.accessToken || "");
          setGoogleProfile(res.data.profile || null);

          const cachedAvatar = sessionStorage.getItem(
            `pagetest.avatarUrl:${user.id || user.email}`,
          );
          if (cachedAvatar) {
            setAvatarUrl(cachedAvatar);
            return;
          }

          const token = localStorage.getItem("token");
          if (!token) {
            setAvatarUrl(null);
            return;
          }

          axios
            .get(`${import.meta.env.VITE_BACKEND_URL}/api/auth/avatar-url`, {
              headers: { Authorization: `Bearer ${token}` },
            })
            .then((av) => {
              const url = av.data.url;
              setAvatarUrl(url);
              if (url && (user.id || user.email)) {
                sessionStorage.setItem(
                  `pagetest.avatarUrl:${user.id || user.email}`,
                  url,
                );
              }
            })
            .catch(() => setAvatarUrl(null));
        })
        .catch(() => {
          setGoogleConnected(false);
          setGoogleAccessToken("");
          setGoogleProfile(null);
          setAvatarUrl(null);
        });
    } else {
      setGoogleConnected(false);
      setGoogleAccessToken("");
      setGoogleProfile(null);
      setAvatarUrl(null);
    }
  }, [isAuthenticated, user?.email, user?.id]);

  useEffect(() => {
    if (!isAuthenticated) {
      setGoogleConnected(false);
      setGoogleAccessToken("");
      setGoogleProfile(null);
      setAvatarUrl(null);
      clearAvatarSessionCache();
    }
  }, [isAuthenticated]);

  // Use proxied avatar when available, else fall back to direct Google URL
  const profilePicture = avatarUrl || googleProfile?.picture;

  return (
    <GoogleAuthContext.Provider
      value={{
        googleConnected,
        googleProfile,
        googleAccessToken,
        profilePicture, // Prefer proxied URL to avoid 429 from Google
        setGoogleProfile,
        setGoogleConnected,
        setGoogleAccessToken,
        clearAvatarCache: () => {
          clearAvatarSessionCache(user?.id || user?.email);
          setAvatarUrl(null);
        },
      }}
    >
      {children}
    </GoogleAuthContext.Provider>
  );
}

export function useGoogleAuth() {
  const context = useContext(GoogleAuthContext);
  if (!context) {
    throw new Error("useGoogleAuth must be used within a GoogleAuthProvider");
  }
  return context;
}
